import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { apiGet, apiRequest } from '../../../utils/fetch';
import { TokenPlanInterface, GenericResponseInterface } from '../../../interfaces/interfaces';
import { getAdSpaceTokenPlansColumns } from './TokenPlansColumns';
import { AdSpaceTokenPlansUpdateDialog } from './TokenPlansUpdateDialog';

const AdSpaceTokenPlansAdminList: React.FC = () => {
    const [plans, setPlans] = useState<TokenPlanInterface[]>([]);
    const [plan, setPlan] = useState<TokenPlanInterface | null>(null);
    const [planId, setPlanId] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [planToDelete, setPlanToDelete] = useState<TokenPlanInterface | null>(null);
    const [updateDialogOpen, setUpdateDialogOpen] = useState(false);
    const [planToUpdate, setPlanToUpdate] = useState<TokenPlanInterface | null>(null);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);

    // Fetch all plans
    const handleGetAllPlans = async () => {
        setError(null);
        setPlan(null);
        setSuccess(null);
        try {
            const result = await apiGet<GenericResponseInterface<TokenPlanInterface[]>>('/tokenPlans');
            if (result.status && result.data) {
                setPlans(result.data);
            } else {
                setError(result.message || 'Failed to fetch token plans');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch token plans');
        }
    };

    // Fetch plan by id
    const handleGetPlanById = async () => {
        setError(null);
        setPlans([]);
        setSuccess(null);
        if (!planId) return setError('Please enter a Token Plan ID');
        try {
            const result = await apiGet<GenericResponseInterface<TokenPlanInterface>>(`/tokenPlans/${planId}`);
            if (result.status && result.data) {
                setPlan(result.data);
            } else {
                setError(result.message || 'Failed to fetch token plan');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to fetch token plan');
        }
    };

    // Delete plan
    const handleDeletePlan = async () => {
        if (!planToDelete) return;
        setError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface>(`/tokenPlans/delete/${planToDelete.id}`, 'DELETE');
            if (result.status) {
                setSuccess(result.message || 'Token plan deleted successfully');
                setDeleteDialogOpen(false);
                setPlanToDelete(null);
                handleGetAllPlans();
            } else {
                setError(result.message || 'Failed to delete token plan');
            }
        } catch (err: any) {
            setError(err.message || 'Failed to delete token plan');
        }
    };

    // Update plan
    const handleUpdatePlan = async () => {
        if (!planToUpdate || !planToUpdate.id) return;
        setUpdateError(null);
        setSuccess(null);
        try {
            const result = await apiRequest<GenericResponseInterface<TokenPlanInterface>>(
                `/tokenPlans/update/${planToUpdate.id}`,
                'PUT',
                planToUpdate
            );
            if (result.status) {
                setSuccess(result.message || 'Token plan updated successfully');
                setUpdateDialogOpen(false);
                setPlanToUpdate(null);
                handleGetAllPlans();
            } else {
                setUpdateError(result.message || 'Failed to update token plan');
            }
        } catch (err: any) {
            setUpdateError(err.message || 'Failed to update token plan');
        }
    };

    const columns = getAdSpaceTokenPlansColumns(
        setPlanToDelete,
        setDeleteDialogOpen,
        setPlanToUpdate,
        setUpdateDialogOpen
    );

    useEffect(() => {
        handleGetAllPlans();
    }, []);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetAllPlans}>Get All Token Plans</Button>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Button variant="contained" onClick={handleGetPlanById}>Get Plan by ID</Button>
                <TextField
                    label="Token Plan ID"
                    value={planId}
                    onChange={e => setPlanId(e.target.value)}
                    size="small"
                />
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
            <Paper sx={{ width: '100%', overflow: 'auto', mt: 2 }}>
                {plans.length > 0 && (
                    <DataGrid
                        rows={plans.map(a => ({ ...a, id: a.id ?? Math.random() }))}
                        columns={columns}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10, page: 0 } }
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        autoHeight
                    />
                )}
                {plan && (
                    <DataGrid
                        rows={[{ ...plan, id: plan.id ?? Math.random() }]}
                        columns={columns}
                        hideFooter
                        autoHeight
                    />
                )}
            </Paper>
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    Are you sure you want to delete this token plan?
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button color="error" onClick={handleDeletePlan}>Delete</Button>
                </DialogActions>
            </Dialog>
            <AdSpaceTokenPlansUpdateDialog
                open={updateDialogOpen}
                planToUpdate={planToUpdate}
                setPlanToUpdate={setPlanToUpdate}
                onClose={() => {
                    setUpdateDialogOpen(false);
                    setSuccess(null);
                }}
                onSave={handleUpdatePlan}
                updateError={updateError}
                updateSuccess={success}
            />
        </Box>
    );
};

export default AdSpaceTokenPlansAdminList;